import { useState, useEffect } from 'react';
import { useOrg } from '../contexts/OrgContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { InspectionMode, Role, OrganizationRiskMatrix, DEFAULT_RISK_MATRIX } from '@easydone/shared';
import { CogIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { RiskMatrixGrid } from '../components/RiskMatrixGrid';

export function OrganizationSettingsPage() {
  const { currentOrg } = useOrg();
  const { userData } = useAuth();

  const [inspectionMode, setInspectionMode] = useState<InspectionMode>('asset-primary');
  const [autoCreateAssets, setAutoCreateAssets] = useState(false);
  const [riskMatrix, setRiskMatrix] = useState<OrganizationRiskMatrix>(DEFAULT_RISK_MATRIX);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (currentOrg?.settings?.inspection?.mode) {
      setInspectionMode(currentOrg.settings.inspection.mode);
    }
    if (currentOrg?.settings?.inspection?.autoCreateAssets !== undefined) {
      setAutoCreateAssets(currentOrg.settings.inspection.autoCreateAssets);
    }
    if (currentOrg?.settings?.riskMatrix) {
      setRiskMatrix(currentOrg.settings.riskMatrix);
    }
  }, [currentOrg]);

  const handleSave = async () => {
    if (!currentOrg) return;

    // Check if user has permission to modify settings
    if (userData?.role !== Role.ORG_ADMIN) {
      setError('You do not have permission to modify organization settings');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const orgRef = doc(db, 'organizations', currentOrg.id);
      await updateDoc(orgRef, {
        'settings.inspection': {
          mode: inspectionMode,
          autoCreateAssets: autoCreateAssets,
        },
        'settings.riskMatrix': riskMatrix,
        updatedAt: new Date(),
      });

      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Failed to save settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const isAdmin = userData?.role === Role.ORG_ADMIN;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <CogIcon className="w-8 h-8 mr-3" />
          Organization Settings
        </h1>
        <p className="text-gray-600 mt-1">
          Configure how your organization uses EasyDone-Pro
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
          <p className="text-sm text-green-800">{successMessage}</p>
        </div>
      )}

      <div className="card space-y-6">
        {/* Organization Info */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Organization Information</h2>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div>
              <span className="text-sm text-gray-600">Name: </span>
              <span className="text-sm font-medium text-gray-900">{currentOrg.name}</span>
            </div>
            <div>
              <span className="text-sm text-gray-600">Tier: </span>
              <span className="text-sm font-medium text-gray-900">{currentOrg.tier}</span>
            </div>
            <div>
              <span className="text-sm text-gray-600">Status: </span>
              <span className="text-sm font-medium text-gray-900">{currentOrg.subscriptionStatus}</span>
            </div>
          </div>
        </div>

        {/* Inspection Mode Settings */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Inspection Mode</h2>
          <p className="text-sm text-gray-600 mb-4">
            Choose how your organization tracks inspections and assets.
          </p>

          <div className="space-y-4">
            {/* Asset-Primary Mode */}
            <label className={`block border-2 rounded-lg p-4 cursor-pointer transition-colors ${
              inspectionMode === 'asset-primary'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <div className="flex items-start">
                <input
                  type="radio"
                  value="asset-primary"
                  checked={inspectionMode === 'asset-primary'}
                  onChange={(e) => setInspectionMode(e.target.value as InspectionMode)}
                  disabled={!isAdmin}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Asset-Primary Mode (Default)</div>
                  <p className="text-sm text-gray-600 mt-1">
                    Full asset tracking with inspections tied to specific equipment. Functional locations (FLOCs)
                    serve as organizational structure where assets can be installed.
                  </p>
                  <ul className="mt-2 text-sm text-gray-600 list-disc list-inside space-y-1">
                    <li>Track individual assets with serial numbers</li>
                    <li>Link inspections to specific equipment</li>
                    <li>Commission assets to FLOC positions</li>
                    <li>Best for organizations managing equipment lifecycle</li>
                  </ul>
                </div>
              </div>
            </label>

            {/* FLOC-Only Mode */}
            <label className={`block border-2 rounded-lg p-4 cursor-pointer transition-colors ${
              inspectionMode === 'floc-only'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <div className="flex items-start">
                <input
                  type="radio"
                  value="floc-only"
                  checked={inspectionMode === 'floc-only'}
                  onChange={(e) => setInspectionMode(e.target.value as InspectionMode)}
                  disabled={!isAdmin}
                  className="mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">FLOC-Only Mode (Simplified)</div>
                  <p className="text-sm text-gray-600 mt-1">
                    Simplified workflow where inspections and maintenance are performed directly on functional
                    locations without separate asset tracking.
                  </p>
                  <ul className="mt-2 text-sm text-gray-600 list-disc list-inside space-y-1">
                    <li>No separate asset management</li>
                    <li>Inspections tied directly to FLOCs</li>
                    <li>Simpler data model and workflows</li>
                    <li>Best for location-based maintenance programs</li>
                  </ul>
                </div>
              </div>
            </label>
          </div>

          {/* Auto-Create Assets Toggle */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <label className={`flex items-start cursor-pointer ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input
                type="checkbox"
                checked={autoCreateAssets}
                onChange={(e) => setAutoCreateAssets(e.target.checked)}
                disabled={!isAdmin}
                className="mt-1 mr-3"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">Auto-Create Placeholder Assets</div>
                <p className="text-sm text-gray-600 mt-1">
                  Automatically create temporary assets when inspecting FLOCs that don't have assets installed.
                </p>
                <ul className="mt-2 text-sm text-gray-600 list-disc list-inside space-y-1">
                  <li>Progressive data quality: Start with minimal info, add details over time</li>
                  <li>Supports rotating equipment: Swap assets in/out of FLOCs</li>
                  <li>Flexible: Use full asset tracking for critical areas, simple placeholders elsewhere</li>
                  <li>Natural workflow: Scan nameplates later to enrich asset details</li>
                </ul>
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>How it works:</strong> When creating an inspection for a FLOC without an asset,
                    the system will create a temporary asset with the FLOC's name and a placeholder serial number.
                    You can update this asset later with real equipment details.
                  </p>
                </div>
              </div>
            </label>
          </div>

          {/* Warning for mode changes */}
          {currentOrg.settings?.inspection?.mode && inspectionMode !== currentOrg.settings.inspection.mode && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 font-medium mb-2">
                Warning: Changing inspection mode
              </p>
              <p className="text-sm text-yellow-700">
                This will affect how new inspections are created. Existing inspections will not be modified,
                but users will see different options when creating new inspections.
              </p>
            </div>
          )}
        </div>

        {/* Risk Matrix Settings */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Risk Matrix Configuration</h2>
          <p className="text-sm text-gray-600 mb-4">
            Configure how risk scores are calculated and categorized. Risk Score = Criticality × Condition.
          </p>

          <div className="space-y-6">
            {/* Enable/Disable Risk Matrix */}
            <label className={`flex items-start cursor-pointer ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input
                type="checkbox"
                checked={riskMatrix.enabled}
                onChange={(e) => setRiskMatrix({ ...riskMatrix, enabled: e.target.checked })}
                disabled={!isAdmin}
                className="mt-1 mr-3"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">Enable Risk Matrix</div>
                <p className="text-sm text-gray-600 mt-1">
                  Use risk scoring to prioritize maintenance and inspections based on criticality and condition.
                </p>
              </div>
            </label>

            {/* Risk Thresholds Configuration */}
            {riskMatrix.enabled && (
              <div className="pl-7 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Risk Level Thresholds</h3>
                  <p className="text-xs text-gray-600 mb-4">
                    Configure the score ranges for each risk level. Risk scores range from 1-25.
                  </p>

                  {/* Low Risk */}
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">Low Risk (Green)</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                        1 - {riskMatrix.thresholds.low.max}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Max Score</label>
                        <input
                          type="number"
                          min="1"
                          max="25"
                          value={riskMatrix.thresholds.low.max}
                          onChange={(e) => setRiskMatrix({
                            ...riskMatrix,
                            thresholds: {
                              ...riskMatrix.thresholds,
                              low: { ...riskMatrix.thresholds.low, max: parseInt(e.target.value) || 1 }
                            }
                          })}
                          disabled={!isAdmin}
                          className="input input-sm w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Label</label>
                        <input
                          type="text"
                          value={riskMatrix.thresholds.low.label}
                          onChange={(e) => setRiskMatrix({
                            ...riskMatrix,
                            thresholds: {
                              ...riskMatrix.thresholds,
                              low: { ...riskMatrix.thresholds.low, label: e.target.value }
                            }
                          })}
                          disabled={!isAdmin}
                          className="input input-sm w-full"
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs text-gray-600 mb-1">Recommended Action</label>
                      <input
                        type="text"
                        value={riskMatrix.thresholds.low.action}
                        onChange={(e) => setRiskMatrix({
                          ...riskMatrix,
                          thresholds: {
                            ...riskMatrix.thresholds,
                            low: { ...riskMatrix.thresholds.low, action: e.target.value }
                          }
                        })}
                        disabled={!isAdmin}
                        className="input input-sm w-full"
                      />
                    </div>
                  </div>

                  {/* Medium Risk */}
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">Medium Risk (Yellow)</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                        {riskMatrix.thresholds.low.max + 1} - {riskMatrix.thresholds.medium.max}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Max Score</label>
                        <input
                          type="number"
                          min="1"
                          max="25"
                          value={riskMatrix.thresholds.medium.max}
                          onChange={(e) => setRiskMatrix({
                            ...riskMatrix,
                            thresholds: {
                              ...riskMatrix.thresholds,
                              medium: { ...riskMatrix.thresholds.medium, max: parseInt(e.target.value) || 1 }
                            }
                          })}
                          disabled={!isAdmin}
                          className="input input-sm w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Label</label>
                        <input
                          type="text"
                          value={riskMatrix.thresholds.medium.label}
                          onChange={(e) => setRiskMatrix({
                            ...riskMatrix,
                            thresholds: {
                              ...riskMatrix.thresholds,
                              medium: { ...riskMatrix.thresholds.medium, label: e.target.value }
                            }
                          })}
                          disabled={!isAdmin}
                          className="input input-sm w-full"
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs text-gray-600 mb-1">Recommended Action</label>
                      <input
                        type="text"
                        value={riskMatrix.thresholds.medium.action}
                        onChange={(e) => setRiskMatrix({
                          ...riskMatrix,
                          thresholds: {
                            ...riskMatrix.thresholds,
                            medium: { ...riskMatrix.thresholds.medium, action: e.target.value }
                          }
                        })}
                        disabled={!isAdmin}
                        className="input input-sm w-full"
                      />
                    </div>
                  </div>

                  {/* High Risk */}
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">High Risk (Orange)</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-300">
                        {riskMatrix.thresholds.medium.max + 1} - {riskMatrix.thresholds.high.max}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Max Score</label>
                        <input
                          type="number"
                          min="1"
                          max="25"
                          value={riskMatrix.thresholds.high.max}
                          onChange={(e) => setRiskMatrix({
                            ...riskMatrix,
                            thresholds: {
                              ...riskMatrix.thresholds,
                              high: { ...riskMatrix.thresholds.high, max: parseInt(e.target.value) || 1 }
                            }
                          })}
                          disabled={!isAdmin}
                          className="input input-sm w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Label</label>
                        <input
                          type="text"
                          value={riskMatrix.thresholds.high.label}
                          onChange={(e) => setRiskMatrix({
                            ...riskMatrix,
                            thresholds: {
                              ...riskMatrix.thresholds,
                              high: { ...riskMatrix.thresholds.high, label: e.target.value }
                            }
                          })}
                          disabled={!isAdmin}
                          className="input input-sm w-full"
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs text-gray-600 mb-1">Recommended Action</label>
                      <input
                        type="text"
                        value={riskMatrix.thresholds.high.action}
                        onChange={(e) => setRiskMatrix({
                          ...riskMatrix,
                          thresholds: {
                            ...riskMatrix.thresholds,
                            high: { ...riskMatrix.thresholds.high, action: e.target.value }
                          }
                        })}
                        disabled={!isAdmin}
                        className="input input-sm w-full"
                      />
                    </div>
                  </div>

                  {/* Critical Risk */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">Critical Risk (Red)</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                        {riskMatrix.thresholds.high.max + 1} - {riskMatrix.thresholds.critical.max}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Max Score</label>
                        <input
                          type="number"
                          min="1"
                          max="25"
                          value={riskMatrix.thresholds.critical.max}
                          onChange={(e) => setRiskMatrix({
                            ...riskMatrix,
                            thresholds: {
                              ...riskMatrix.thresholds,
                              critical: { ...riskMatrix.thresholds.critical, max: parseInt(e.target.value) || 1 }
                            }
                          })}
                          disabled={!isAdmin}
                          className="input input-sm w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Label</label>
                        <input
                          type="text"
                          value={riskMatrix.thresholds.critical.label}
                          onChange={(e) => setRiskMatrix({
                            ...riskMatrix,
                            thresholds: {
                              ...riskMatrix.thresholds,
                              critical: { ...riskMatrix.thresholds.critical, label: e.target.value }
                            }
                          })}
                          disabled={!isAdmin}
                          className="input input-sm w-full"
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs text-gray-600 mb-1">Recommended Action</label>
                      <input
                        type="text"
                        value={riskMatrix.thresholds.critical.action}
                        onChange={(e) => setRiskMatrix({
                          ...riskMatrix,
                          thresholds: {
                            ...riskMatrix.thresholds,
                            critical: { ...riskMatrix.thresholds.critical, action: e.target.value }
                          }
                        })}
                        disabled={!isAdmin}
                        className="input input-sm w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-blue-900 mb-2">About Risk Scoring</h4>
                  <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                    <li>Risk Score = Criticality Value (2-5) × Condition Score (1-5)</li>
                    <li>Low Criticality = 2, Medium = 3, High = 4, Critical = 5</li>
                    <li>Condition: 1 = Very good, 2 = Good, 3 = Moderate, 4 = Poor, 5 = Very poor/failed</li>
                    <li>Score ranges from 2 (Low × Very good) to 25 (Critical × Very poor)</li>
                    <li>Configure thresholds to match your organization's risk tolerance</li>
                  </ul>
                </div>

                {/* Visual Risk Matrix */}
                <div className="bg-white rounded-lg border-2 border-gray-300 p-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Risk Matrix Visualization</h3>
                  <p className="text-xs text-gray-600 mb-4">
                    This matrix shows how risk scores are calculated and categorized based on your configuration.
                  </p>
                  <RiskMatrixGrid riskMatrix={riskMatrix} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        {isAdmin && (
          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={
                saving ||
                (inspectionMode === currentOrg.settings?.inspection?.mode &&
                 autoCreateAssets === (currentOrg.settings?.inspection?.autoCreateAssets || false))
              }
              className="btn btn-primary"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}

        {!isAdmin && (
          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Only organization owners and administrators can modify these settings.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
